import { Label } from "@/ui/label";
import { cn } from "@/lib/utils";
import { TaxCodeLookupHint } from "@/components/TaxCodeLookupHint";
import { isValidTaxCode, useCompanyAutoFill } from "@/lib/company-lookup";
import type { CustomerBookingInvoiceType } from "./api";
import { InvoiceField } from "./InvoiceField";
import { PersonalBuyerFields } from "./PersonalBuyerFields";
import type { PersonBuyer } from "./personalInvoiceBuyer";

/** Thông tin công ty bên mua trên hóa đơn doanh nghiệp. */
export type InvoiceBuyer = {
  companyName: string;
  taxCode: string;
  companyAddress: string;
  email: string;
};

export const emptyInvoiceBuyer: InvoiceBuyer = {
  companyName: "",
  taxCode: "",
  companyAddress: "",
  email: "",
};

/** Khuôn mã số thuế nằm ở `@/lib/company-lookup` cùng phần tra cứu: phần kiểm
 *  lỗi của ô và phần quyết định "đã đủ mã để đi tra chưa" phải là MỘT khuôn,
 *  hai bản sao sẽ trôi khỏi nhau đúng lúc không ai để ý. */
/** Đủ chặt để bắt lỗi gõ, đủ lỏng để không từ chối email hợp lệ — máy chủ mới
 *  là nơi kiểm cuối bằng net/mail. */
const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export type InvoiceFieldErrors = Partial<Record<keyof InvoiceBuyer, string>>;

/**
 * Lỗi của từng ô, tính lại mỗi lần gõ.
 *
 * Chọn hóa đơn cá nhân thì không có ô công ty nào để sai — máy chủ bỏ qua và lưu NULL, nên trang
 * cũng không được chặn khách vì dữ liệu nó sẽ không gửi.
 */
export function validateInvoiceBuyer(
  invoiceType: CustomerBookingInvoiceType,
  buyer: InvoiceBuyer,
): InvoiceFieldErrors {
  if (invoiceType !== "business") return {};
  const errors: InvoiceFieldErrors = {};
  if (!buyer.companyName.trim()) errors.companyName = "Nhập tên công ty trên hóa đơn";
  const taxCode = buyer.taxCode.trim();
  if (!taxCode) errors.taxCode = "Nhập mã số thuế";
  else if (!isValidTaxCode(taxCode))
    errors.taxCode = "Mã số thuế gồm 10 chữ số, chi nhánh thêm -001";
  if (!buyer.companyAddress.trim()) errors.companyAddress = "Nhập địa chỉ công ty";
  const email = buyer.email.trim();
  if (!email) errors.email = "Nhập email nhận hóa đơn";
  else if (!emailPattern.test(email)) errors.email = "Email chưa đúng định dạng";
  return errors;
}

export function invoiceBuyerIsComplete(
  invoiceType: CustomerBookingInvoiceType,
  buyer: InvoiceBuyer,
): boolean {
  return Object.keys(validateInvoiceBuyer(invoiceType, buyer)).length === 0;
}

type Props = {
  invoiceType: CustomerBookingInvoiceType;
  onInvoiceTypeChange: (value: CustomerBookingInvoiceType) => void;
  buyer: InvoiceBuyer;
  onBuyerChange: (value: InvoiceBuyer) => void;
  /** Số hóa đơn bán lẻ cá nhân. Ô riêng, KHÔNG nằm trong `buyer`: hai nhánh hóa
   *  đơn loại trừ nhau, gộp vào một type là bắt `validateInvoiceBuyer` phải
   *  biết tới một ô nó không hề kiểm. Vắng mặt = màn này không hỏi mã. */
  eshopCode?: string;
  onEshopCodeChange?: (value: string) => void;
  /** Chỉ tô đỏ sau khi khách đã bấm gửi — không mắng khách ngay khi ô còn trống. */
  showErrors: boolean;
  /** Người mua cá nhân của hoá đơn eShop (CEO 24/10/2026). Vắng mặt = màn này
   *  không hỏi (ô tích «Khách không cung cấp…» không hiện). */
  person?: PersonBuyer;
  onPersonChange?: (value: PersonBuyer) => void;
  /** Hỏi CCCD / hộ chiếu. Trang đặt lịch công khai KHÔNG hỏi. */
  askPersonId?: boolean;
  /** Khoá phần người mua (hoá đơn đã xuất từ hôm trước — CEO 6b). */
  buyerLocked?: boolean;
  /** Trang khách tự đặt — lời của ô tích nói với khách. */
  forCustomer?: boolean;
  /** Tiền tố cho `id` của bốn ô. Khối này dùng ở cả trang đặt lịch công khai lẫn
   *  màn tạo/sửa đơn trong ERP; id trùng nhau thì `htmlFor` của nhãn trỏ nhầm ô
   *  và người dùng bàn phím bấm nhãn lại nhảy sang chỗ khác. */
  idPrefix?: string;
};

/**
 * Chọn hóa đơn cá nhân hoặc doanh nghiệp ở bước xác nhận.
 *
 * Ở đây KHÔNG có ô bật/tắt VAT: mọi lịch hẹn đều chịu thuế, khách chỉ chọn
 * nhận hóa đơn bán lẻ hay hóa đơn doanh nghiệp.
 */
export function BookingInvoiceSection({
  invoiceType,
  onInvoiceTypeChange,
  buyer,
  onBuyerChange,
  eshopCode,
  onEshopCodeChange,
  person,
  onPersonChange,
  askPersonId = false,
  buyerLocked = false,
  forCustomer = false,
  showErrors,
  idPrefix = "public-booking",
}: Props) {
  const errors = validateInvoiceBuyer(invoiceType, buyer);
  const showError = (field: keyof InvoiceBuyer) => (showErrors ? errors[field] : undefined);

  const setField = (field: keyof InvoiceBuyer, value: string) =>
    onBuyerChange({ ...buyer, [field]: value });

  return (
    <div className="space-y-3">
      <Label>Xuất hóa đơn</Label>
      <div className="grid gap-2">
        <InvoiceTypeButton
          selected={invoiceType === "personal"}
          title="Hóa đơn cá nhân"
          description="Hóa đơn theo thông tin người mua cá nhân."
          onClick={() => onInvoiceTypeChange("personal")}
          disabled={buyerLocked}
        />
        <InvoiceTypeButton
          selected={invoiceType === "business"}
          title="Hóa đơn doanh nghiệp"
          description="Hóa đơn cho công ty — cần mã số thuế và thông tin doanh nghiệp."
          onClick={() => onInvoiceTypeChange("business")}
          disabled={buyerLocked}
        />
      </div>

      {invoiceType === "personal" && person && onPersonChange && (
        <div className="rounded-2xl border bg-muted/30 p-3">
          <PersonalBuyerFields
            idPrefix={idPrefix}
            person={person}
            onChange={onPersonChange}
            showErrors={showErrors}
            askId={askPersonId}
            disabled={buyerLocked}
            forCustomer={forCustomer}
          />
        </div>
      )}

      {invoiceType === "personal" && onEshopCodeChange && (
        <div className="rounded-2xl border bg-muted/30 p-3">
          <InvoiceField
            id={`${idPrefix}-invoice-eshop-code`}
            label="Mã hóa đơn cá nhân"
            value={eshopCode ?? ""}
            onChange={onEshopCodeChange}
            placeholder="HD00012345"
            maxLength={50}
          />
          <p className="mt-1.5 text-xs leading-relaxed text-muted-foreground">
            Không bắt buộc — điền khi đã có mã hóa đơn.
          </p>
        </div>
      )}

      {invoiceType === "business" && (
        <VatBuyerFields
          idPrefix={idPrefix}
          buyer={buyer}
          onBuyerChange={onBuyerChange}
          setField={setField}
          showError={showError}
        />
      )}
    </div>
  );
}

/**
 * Bốn ô doanh nghiệp của hóa đơn doanh nghiệp.
 *
 * Tách khỏi `BookingInvoiceSection` để phần tra mã số thuế nằm gọn một chỗ:
 * hook chỉ chạy khi nhánh VAT thật sự đang mở, và hàm cha không phình ra.
 */
function VatBuyerFields({
  idPrefix,
  buyer,
  onBuyerChange,
  setField,
  showError,
}: {
  idPrefix: string;
  buyer: InvoiceBuyer;
  onBuyerChange: (value: InvoiceBuyer) => void;
  setField: (field: keyof InvoiceBuyer, value: string) => void;
  showError: (field: keyof InvoiceBuyer) => string | undefined;
}) {
  // Gõ đủ mã số thuế, ngừng tay nửa giây → tên công ty và địa chỉ tự điền vào
  // hai ô đang trống. Gắn ở ĐÂY nên cả bốn màn có bốn ô này đều được: trang đặt
  // lịch công khai, tab đặt lịch của CSKH, và hai khối hóa đơn của đơn hàng —
  // cả bốn đều dựng lại chính khối này.
  const { lookup, showUse, applyLookup } = useCompanyAutoFill({
    taxCode: buyer.taxCode,
    enabled: true,
    name: buyer.companyName,
    address: buyer.companyAddress,
    // MỘT lời ghi cho cả hai ô: `onBuyerChange` nhận cả object, nên hai lời gọi
    // liên tiếp cùng đọc `buyer` của lần dựng này thì lời sau xoá mất lời trước.
    onFill: (patch) =>
      onBuyerChange({
        ...buyer,
        ...(patch.name !== undefined ? { companyName: patch.name } : {}),
        ...(patch.address !== undefined ? { companyAddress: patch.address } : {}),
      }),
  });

  return (
    <div className="grid gap-3 rounded-2xl border bg-muted/30 p-3 md:grid-cols-2">
      <InvoiceField
        id={`${idPrefix}-invoice-company`}
        label="Tên công ty"
        value={buyer.companyName}
        onChange={(value) => setField("companyName", value)}
        error={showError("companyName")}
        placeholder="Công ty TNHH ABC"
        autoComplete="organization"
        className="md:col-span-2"
      />
      <InvoiceField
        id={`${idPrefix}-invoice-tax`}
        label="Mã số thuế"
        value={buyer.taxCode}
        onChange={(value) => setField("taxCode", value)}
        error={showError("taxCode")}
        placeholder="0312345678"
        // Ô chữ, KHÔNG phải ô số: type="number" ăn mất số 0 đầu của MST.
        inputMode="numeric"
        // Ô đang bị tô đỏ thì im: hai dòng chữ chồng nhau dưới cùng một ô
        // là bắt người nhập tự đoán dòng nào mới là việc phải làm.
        hint={
          showError("taxCode") ? undefined : (
            <TaxCodeLookupHint lookup={lookup} showUse={showUse} onUse={applyLookup} />
          )
        }
      />
      <InvoiceField
        id={`${idPrefix}-invoice-email`}
        label="Email nhận hóa đơn"
        value={buyer.email}
        onChange={(value) => setField("email", value)}
        error={showError("email")}
        placeholder="ketoan@congty.vn"
        type="email"
        autoComplete="email"
      />
      <InvoiceField
        id={`${idPrefix}-invoice-address`}
        label="Địa chỉ công ty"
        value={buyer.companyAddress}
        onChange={(value) => setField("companyAddress", value)}
        error={showError("companyAddress")}
        placeholder="Số nhà, đường, phường, quận, tỉnh/thành"
        autoComplete="street-address"
        className="md:col-span-2"
      />
      <p className="text-xs leading-relaxed text-muted-foreground md:col-span-2">
        Hóa đơn được gửi tới email này sau khi hoàn tất buổi chụp.
      </p>
    </div>
  );
}

function InvoiceTypeButton({
  selected,
  title,
  description,
  onClick,
  disabled,
}: {
  selected: boolean;
  title: string;
  description: string;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={selected}
      disabled={disabled}
      className={cn(
        "min-h-11 rounded-2xl border bg-surface p-3 text-left transition disabled:opacity-60",
        selected ? "border-primary bg-primary/5" : "hover:border-primary/40",
      )}
    >
      <div className="text-sm font-medium">{title}</div>
      <div className="text-xs text-muted-foreground">{description}</div>
    </button>
  );
}

/**
 * Nhãn tiếng Việt của một loại hóa đơn, dùng chung cho mọi màn chỉ hiển thị
 * (chi tiết đơn, tab đặt lịch của CSKH, màn xác nhận đặt lịch). Nhận `null` vì
 * đơn và booking cũ chưa ghi nhận lựa chọn nào.
 */
export function invoiceTypeLabel(
  invoiceType: CustomerBookingInvoiceType | null | undefined,
): string | null {
  if (invoiceType === "business") return "Hóa đơn doanh nghiệp";
  if (invoiceType === "personal") return "Hóa đơn cá nhân";
  return null;
}
